import { z } from 'zod';
import { EstadoOtSchema } from './ticket.js';
import {
  ACTIVITY_MEDIA_PROCESSING_STATUS,
  ACTIVITY_MEDIA_VIDEO_MIME_TYPES,
} from './activity-media.js';
import { ADJUNTO_TIPOS, ADJUNTO_MIME_TYPES } from './adjunto.js';

export const VerifyLinkSchema = z.object({
  uuid: z.string().uuid(),
  codigo: z.string().regex(/^\d{6}$/),
});

export const MiniAppTicketSummarySchema = z.object({
  id: z.number(),
  correlativo: z.string(),
  descripcion: z.string(),
  equipoDescripcion: z.string().nullable(),
  estadoOt: EstadoOtSchema,
  tipoOtNombre: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
});

// Medición o dato del equipo con su valor (ver schemas/variables.ts). Opcional en las respuestas:
// una API anterior a las mediciones no las trae y se tratan como [].
const MiniAppVariableValorSchema = z.object({
  clave: z.string(),
  etiqueta: z.string(),
  unidad: z.string().nullable(),
  tipo: z.enum(['numero', 'texto']),
  valor: z.string().nullable(),
});

export const MiniAppTicketDetailSchema = z.object({
  id: z.number(),
  correlativo: z.string(),
  descripcion: z.string(),
  // Activity name (planMatriz.nombre ?? servicio ?? null), same convention as
  // the enriched list (planMatrizNombre/servicio) and lider.service — surfaced
  // so coordinator screens can show it next to the correlativo.
  nombre: z.string().nullable(),
  equipoDescripcion: z.string().nullable(),
  estadoOt: EstadoOtSchema,
  tipoOtNombre: z.string().nullable(),
  pdfUrl: z.string().nullable(),
  signedPdfUrl: z.string().nullable(),
  images: z.array(z.object({
    id: z.number(),
    url: z.string(),
    thumbnailUrl: z.string().nullable(),
    descripcion: z.string().nullable(),
  })),
  // Activity-level videos available for supervisor playback in the mini-app.
  // Top-level OT photos (Ot.imagenes) go into the signed PDF as compliance
  // artifact; activity-level photos (DetalleActividadFoto rows where
  // mimeType starts with image/) have always lived outside the PDF.
  // Activity-level videos are surfaced here for supervisor review pre-approval
  // and do NOT enter the PDF (no static frame can substitute for the clip).
  //
  // The renderer MUST branch on processingStatus and only show a <video> when
  // status === 'COMPLETED'. While PENDING/PROCESSING the optimizedS3Key is
  // null and serving raw HEVC .mov to Chrome Android renders a silent black
  // box (lesson_apoderapp_natali_3_silent_failures_2026_05_07). FAILED needs
  // an explicit error message — falling through to raw playback is worse
  // than acknowledging the failure.
  activityVideos: z.array(z.object({
    id: z.number().int().positive(),
    url: z.string(),
    thumbnailUrl: z.string().nullable(),
    mimeType: z.enum(ACTIVITY_MEDIA_VIDEO_MIME_TYPES),
    actividadDescripcion: z.string().nullable(),
    processingStatus: z.enum(ACTIVITY_MEDIA_PROCESSING_STATUS).nullable(),
  })).default([]),
  // Certificados / respaldos / fotos sueltas adjuntadas via AdjuntosTab.
  // No entran al PDF firmado (compliance artifact) — son anexos viewable
  // por el coordinador. Asimetría intencional con Ot.imagenes (al PDF) y
  // DetalleActividadFoto (videos de la mini-app, fotos al PDF).
  //
  // url puede ser null cuando el objeto S3 no existe (presigned generation
  // toleró el huérfano via safePresignedUrl) — el renderer debe mostrar
  // un placeholder "Archivo no disponible" en ese caso en vez de un link
  // 404 silencioso (lesson_sime_chrome_orb_blocks_orphan_thumbnails_2026_05_15).
  certificados: z.array(z.object({
    id: z.number().int().positive(),
    // Pinned to the upload whitelist so frontend TIPO_LABEL / CertIcon switches stay
    // in sync — drift surfaces at the API boundary instead of as silent fallthrough.
    tipo: z.enum(ADJUNTO_TIPOS),
    nombre: z.string(),
    url: z.string().nullable(),
    // Attachment-disposition presigned URL for a real download (vs `url` which
    // is inline-view). Nullable for the same S3-orphan tolerance as `url`.
    downloadUrl: z.string().nullable(),
    mimeType: z.enum(ADJUNTO_MIME_TYPES),
    size: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
  })).default([]),
  checklist: z.array(z.object({
    id: z.number(),
    descripcion: z.string(),
    completado: z.boolean(),
    // Sección del checklist; null/ausente = ítem sin sección.
    seccion: z.string().nullable().optional(),
    // Mediciones del ítem con su valor; [] / ausente = sin mediciones.
    variables: z.array(MiniAppVariableValorSchema).optional(),
  })),
  // Datos del equipo de la OT con su valor; [] / ausente = la pauta no los pide.
  datosEquipo: z.array(MiniAppVariableValorSchema).optional(),
  tooltime: z.array(z.object({
    id: z.number(),
    inicio: z.string().datetime(),
    termino: z.string().datetime(),
    horasHombre: z.number(),
    descripcion: z.string().nullable(),
  })),
});

export const MiniAppTicketEnrichedSchema = z.object({
  // Core identity
  id: z.number(),
  correlativo: z.string(),
  descripcion: z.string(),
  // Equipment / classification
  equipoDescripcion: z.string().nullable(),
  estadoOt: EstadoOtSchema,
  tipoOtNombre: z.string().nullable(),
  // Planning
  planMatrizNombre: z.string().nullable(),
  servicio: z.string().nullable(),
  ubicacionNombre: z.string().nullable(),
  // Scheduled maintenance date (ot.fecha_programada) as ISO string, or null.
  // Coordinator surfaces (T: badge + review board) and the signed PDF show THIS,
  // not ot.fecha — the latter is the "Iniciar" start timestamp, which drifts
  // months when a ticket is executed late (a March plan started in June reads June).
  fechaProgramada: z.string().nullable(),
  // Media & documents
  thumbnailUrl: z.string().nullable(),
  hasPdf: z.boolean(),
  hasSignedPdf: z.boolean(),
  imageCount: z.number().int().nonnegative(),
  // Checklist progress
  checklistTotal: z.number().int().nonnegative(),
  checklistCompleted: z.number().int().nonnegative(),
  // Certificates
  certificadoCount: z.number().int().nonnegative(),
  // Supervisor decision (from LoteAprobacionTicket)
  currentDecision: z.enum(['APROBADO', 'RECHAZADO']).nullable(),
  currentObservacion: z.string().nullable(),
});

export const DecisionSchema = z.object({
  otId: z.number().int().positive(),
  decision: z.enum(['APROBADO', 'RECHAZADO']),
  observacion: z.string().optional(),
}).refine(
  (data) => data.decision !== 'RECHAZADO' || (data.observacion && data.observacion.trim().length > 0),
  { message: 'Observacion requerida para rechazar', path: ['observacion'] },
);

export const SubmitDecisionsSchema = z.object({
  decisions: z.array(DecisionSchema).min(1),
});

// Coordinator signature upload — multipart/form-data content-type validation
export const COORDINATOR_SIGNATURE_ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg'] as const;
export const COORDINATOR_SIGNATURE_MAX_SIZE_BYTES = 500 * 1024; // 500 KB

export const UploadSignatureSchema = z.object({
  mimeType: z.enum(COORDINATOR_SIGNATURE_ALLOWED_MIME_TYPES, {
    errorMap: () => ({ message: 'Tipo de archivo no permitido. Solo se acepta image/png o image/jpeg.' }),
  }),
});

// Respaldos: monthly closure ZIP packages downloadable by the coordinator from
// the miniapp. Scoped to the ámbito of the verified lote — the link+code already
// grant access to that ámbito's signed PDFs, so the package list stays within
// the same data perimeter. Only COMPLETED closures with a generated package are
// listed; legacy periods appear once the contractor runs their retroactive closure.
export const MiniAppRespaldoSchema = z.object({
  closureLogId: z.number().int().positive(),
  periodo: z.string(),
  // approvedCount of the closure — how many signed tickets the ZIP contains.
  ticketCount: z.number().int().nonnegative(),
  packageSize: z.number().int().nonnegative().nullable(),
  packageGeneratedAt: z.string().datetime().nullable(),
  isAddendum: z.boolean(),
});

export const MiniAppRespaldosResponseSchema = z.object({
  ambitoNombre: z.string(),
  respaldos: z.array(MiniAppRespaldoSchema),
});

// Histórico: read-only access to the ámbito's APPROVED tickets from periods that
// were never packaged into a lote. The miniapp ticket list only shows tickets that
// belong to the verified lote, and lotes only exist from the 2026-06 cutover on —
// so ~10k migrated legacy tickets were unreachable even though their signed PDFs
// are in S3. Same ámbito perimeter as Respaldos: the lote's ámbito is authoritative.
//
// Scope is deliberately narrow: only estadoOt APROBADO WITH a signed PDF. That is
// what was executed, signed and invoiced — the contractual record. Tickets left in
// PROGRAMADO/EJECUTADO in closed months are not the coordinator's business here.
// Same strict shape the rest of the repo uses (closure.ts, kpi.ts, lote.ts): month
// 01-12, never 00 or 13. It matters more here than elsewhere — `periodo` is
// interpolated into the S3 package key and into the BullMQ jobId, so a lax pattern
// would let the histórico route mint keys the closure route could never produce.
export const MiniAppPeriodoSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Periodo debe tener formato YYYY-MM');

export const MiniAppHistoricoPeriodoSchema = z.object({
  periodo: MiniAppPeriodoSchema,
  // Approved tickets with a signed PDF in that period — the exact set the
  // ticket list and the ZIP will contain, so the number never contradicts itself.
  ticketCount: z.number().int().nonnegative(),
});

export const MiniAppHistoricoPeriodosResponseSchema = z.object({
  ambitoNombre: z.string(),
  periodos: z.array(MiniAppHistoricoPeriodoSchema),
});

export const MiniAppHistoricoTicketSchema = z.object({
  otId: z.number().int().positive(),
  correlativo: z.string(),
  fechaProgramada: z.string().datetime().nullable(),
  // "Nombre de la Actividad": planMatriz.nombre, falling back to servicio for NPL
  // tickets with no plan — same resolution the ZIP uses for its filenames.
  actividad: z.string().nullable(),
  // What the crew wrote they did. Shown when `actividad` is null, which is NOT an
  // edge case in legacy months: 79 of the 186 approved tickets of 2026-05 have
  // neither plan name nor servicio, while observaciones is populated on ~100% of
  // them. Without this the coordinator would face rows of "Sin actividad".
  // Trimmed server-side: it is free text meant to identify the work at a glance,
  // and the full narrative already lives in the signed PDF.
  descripcion: z.string().nullable(),
  // ECO of the ticket's plan; null for NPL. Mirrors the ZIP's folder grouping so
  // what the coordinator sees on screen matches what they find inside the archive.
  eco: z.string().nullable(),
  // Attached certificates that can actually be OPENED. Counts only rows with a real
  // S3 key: the cutover migrated legacy `ot_certificado` rows with `s3_key = ''`
  // (prod incident 2026-07-27) and in some legacy months NONE of them have a key —
  // INS 2025-09 has 19 rows and 0 openable. A badge offering something that resolves
  // to "Archivo no disponible" is worse than no badge at all.
  certificadoCount: z.number().int().nonnegative(),
});

export const MiniAppHistoricoTicketsResponseSchema = z.object({
  ambitoNombre: z.string(),
  periodo: MiniAppPeriodoSchema,
  tickets: z.array(MiniAppHistoricoTicketSchema),
});

// Package states. There is no job-status table: the S3 object IS the state.
// 'listo' = the ZIP exists (either from a formal closure or a previous on-demand
// build); 'generando' = a packaging job is in flight; 'no_generado' = nothing built
// yet, the coordinator can request it.
//
// A DISCRIMINATED union, not a flat record with nullable fields: 'listo' without a
// downloadUrl has no meaning, and modelling it as possible forces every consumer
// into a defensive branch whose only honest behaviour would be to do nothing. The
// UI had exactly that — a `downloadUrl ?? ''` and a fallback that rendered the
// "generate" button for an already-built package, a dead end with no error shown.
// 'fallido' is a state of its own and not a silent fall back to 'no_generado': the
// queue retries twice and then gives up, and without this the coordinator watched a
// spinner for minutes and then saw the screen quietly return to its initial button —
// indistinguishable from never having pressed it.
export const MINIAPP_PAQUETE_ESTADOS = ['listo', 'generando', 'no_generado', 'fallido'] as const;

const MiniAppPaqueteBaseSchema = z.object({
  periodo: MiniAppPeriodoSchema,
  ticketCount: z.number().int().nonnegative(),
});

export const MiniAppPaqueteEstadoSchema = z.discriminatedUnion('estado', [
  MiniAppPaqueteBaseSchema.extend({
    estado: z.literal('listo'),
    downloadUrl: z.string(),
    // Stays nullable INSIDE 'listo': when the package is found by probing S3 rather
    // than through a ClosureLog row, the size is genuinely unknown. That null is
    // information; a null downloadUrl would have been an impossible state.
    packageSize: z.number().int().nonnegative().nullable(),
  }),
  MiniAppPaqueteBaseSchema.extend({ estado: z.literal('generando') }),
  MiniAppPaqueteBaseSchema.extend({ estado: z.literal('no_generado') }),
  MiniAppPaqueteBaseSchema.extend({ estado: z.literal('fallido') }),
]);

export type VerifyLinkInput = z.infer<typeof VerifyLinkSchema>;
export type MiniAppHistoricoPeriodo = z.infer<typeof MiniAppHistoricoPeriodoSchema>;
export type MiniAppHistoricoPeriodosResponse = z.infer<typeof MiniAppHistoricoPeriodosResponseSchema>;
export type MiniAppHistoricoTicket = z.infer<typeof MiniAppHistoricoTicketSchema>;
export type MiniAppHistoricoTicketsResponse = z.infer<typeof MiniAppHistoricoTicketsResponseSchema>;
export type MiniAppPaqueteEstado = z.infer<typeof MiniAppPaqueteEstadoSchema>;
export type MiniAppRespaldo = z.infer<typeof MiniAppRespaldoSchema>;
export type MiniAppRespaldosResponse = z.infer<typeof MiniAppRespaldosResponseSchema>;
export type MiniAppTicketSummary = z.infer<typeof MiniAppTicketSummarySchema>;
export type MiniAppTicketDetail = z.infer<typeof MiniAppTicketDetailSchema>;
export type MiniAppTicketEnriched = z.infer<typeof MiniAppTicketEnrichedSchema>;
export type Decision = z.infer<typeof DecisionSchema>;
export type SubmitDecisionsInput = z.infer<typeof SubmitDecisionsSchema>;
export type UploadSignatureInput = z.infer<typeof UploadSignatureSchema>;
